import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { FileText, MessageSquarePlus, CreditCard, Tags } from "lucide-react";
import { openBilling } from "@/lib/plans";
import { RequestChangeDialog } from "@/components/RequestChangeDialog";
import { CompanyDetailsDialog } from "@/components/CompanyDetailsDialog";
import { JobTypesDialog } from "@/components/JobTypesDialog";
import { usePay } from "@/context/CompanyContext";
import { periodLabel } from "@/lib/payPeriod";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { useDepot } from "@/context/DepotContext";
import { applyBrandColor, isDarkEnough, BRAND } from "@/lib/brand";
import { CompanyBrandingSection } from "@/components/CompanyBrandingSection";
import { DemoResetSection } from "@/components/DemoResetSection";
import { CompanyModulesSection } from "@/components/CompanyModulesSection";
import { hasModule } from "@/lib/modules";

export const SettingsDialog = ({ open, onOpenChange }) => {
  const { user, setUser, logout } = useAuth();
  const isCoord = user?.role === "coordinator";
  const { company, setCompany } = useCompany();
  const { depot, nameOf } = useDepot();
  const canBrand = isCoord && !!company?.can_edit;
  const jobsOn = hasModule(company, "jobs");
  const scopeName = isCoord && depot ? (nameOf(depot) || user?.depot_name) : "";
  const [companyName, setCompanyName] = useState("");
  const [color, setColor] = useState(BRAND.color);
  const [name, setName] = useState(user?.name || "");
  const [emailAlerts, setEmailAlerts] = useState(user?.email_alerts !== false);
  const [autoEmailClient, setAutoEmailClient] = useState(false);
  const [supervisorEmail, setSupervisorEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmResync, setConfirmResync] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const pay = usePay();

  const resync = async () => {
    setResyncing(true);
    try {
      const r = await api.restoreData({ force: true });
      const c = r.counts || {};
      toast.success(`Loaded ${c.jobs || 0} jobs and ${c.crew || 0} crew from your old app — please sign in again.`);
      setTimeout(() => logout(), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-sync failed");
      setResyncing(false);
    }
  };

  useEffect(() => {
    if (open && canBrand) {
      setCompanyName(company?.name || "");
      setColor(company?.color || BRAND.color);
    }
    if (!open) applyBrandColor(company?.color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (open && canBrand && isDarkEnough(color)) applyBrandColor(color); }, [open, canBrand, color]);

  useEffect(() => {
    if (open && isCoord) {
      api.reportSettings()
        .then((s) => { setAutoEmailClient(!!s.auto_email_client); setSupervisorEmail(s.supervisor_email || user?.email || ""); })
        .catch(() => {});
    }
  }, [open, isCoord, user?.email]);

  const save = async () => {
    if (canBrand && !companyName.trim()) { toast.error("Add your company name"); return; }
    if (canBrand && !isDarkEnough(color)) { toast.error("Pick a darker main colour"); return; }
    setSaving(true);
    try {
      const updated = await api.updatePreferences({ name: name.trim() || undefined, email_alerts: emailAlerts });
      setUser(updated);
      if (isCoord) {
        await api.setReportSettings({ auto_email_client: autoEmailClient, supervisor_email: supervisorEmail.trim() });
        if (canBrand) setCompany(await api.updateCompany({ name: companyName.trim(), color }));
      }
      toast.success("Settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Settings</DialogTitle>
          <DialogDescription>{canBrand ? "Your profile, company branding and email reports." : isCoord ? "Your profile and email reports." : "Update your display name and email notifications."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Display name</Label>
            <Input data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
          </div>
          <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-medium">Email alerts</p>
              <p className="text-xs text-muted-foreground">Get emailed when a job is assigned, updated or completed.</p>
            </div>
            <Switch data-testid="settings-email-alerts-toggle" checked={emailAlerts} onCheckedChange={setEmailAlerts} />
          </div>
          {isCoord && (
            <>
              {canBrand && company?.demo && <DemoResetSection />}
              {canBrand && <CompanyModulesSection />}
              {canBrand && <CompanyBrandingSection name={companyName} setName={setCompanyName} color={color} setColor={setColor} />}
              {canBrand && (
                <div className="rounded-lg border border-border p-4 space-y-2" data-testid="company-details-section">
                  <p className="text-sm font-medium">Payroll &amp; business details</p>
                  <p className="text-xs text-muted-foreground">Pay period ({periodLabel(pay)}), state, payroll email, ABN and address.</p>
                  <Button variant="outline" data-testid="company-details-btn" onClick={() => { onOpenChange(false); setDetailsOpen(true); }} className="rounded-full">Edit payroll settings</Button>
                </div>
              )}
              {jobsOn && <div className="rounded-lg border border-border p-4 space-y-2" data-testid="job-types-section">
                <p className="text-sm font-medium flex items-center gap-2"><Tags className="w-4 h-4 text-primary" /> Job types</p>
                <p className="text-xs text-muted-foreground" data-testid="job-types-summary">{(company?.job_types || []).map((t) => t.label).join(", ")}</p>
                <Button variant="outline" data-testid="job-types-edit-btn" onClick={() => { onOpenChange(false); setTypesOpen(true); }} className="rounded-full">Edit job types</Button>
              </div>}
              {canBrand && company?.billing && (
                <div className="rounded-lg border border-border p-4 space-y-2" data-testid="billing-settings-section">
                  <p className="text-sm font-medium flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary" /> Plan &amp; billing</p>
                  <p className="text-xs text-muted-foreground" data-testid="billing-settings-status">
                    {company.billing.status === "free" ? "Free — nothing to pay."
                      : company.billing.status === "trial" ? `Free trial: ${company.billing.days_left} days left.`
                        : company.billing.plan_name ? `${company.billing.plan_name} plan, billed ${company.billing.cadence}.` : "No active plan."}
                  </p>
                  {company.billing.status !== "free" && <Button variant="outline" data-testid="billing-settings-btn" onClick={() => { onOpenChange(false); openBilling(); }} className="rounded-full">Manage plan</Button>}
                </div>
              )}
              {jobsOn && <>
              {scopeName && <p className="text-xs text-muted-foreground -mb-2" data-testid="settings-depot-scope">The email settings below apply to the <span className="font-semibold text-foreground">{scopeName}</span> depot.</p>}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
                <div>
                  <p className="text-sm font-medium">Auto-email client on completion</p>
                  <p className="text-xs text-muted-foreground">When a job is marked complete, automatically email the report to the client (only if their client toggle is on).</p>
                </div>
                <Switch data-testid="settings-auto-email-toggle" checked={autoEmailClient} onCheckedChange={setAutoEmailClient} />
              </div>
              <div className="space-y-2">
                <Label>Supervisor completion copy</Label>
                <Input data-testid="settings-supervisor-email" type="email" value={supervisorEmail} onChange={(e) => setSupervisorEmail(e.target.value)} placeholder="supervisor@yourcompany.com.au" />
                <p className="text-xs text-muted-foreground">Every completed job's report is emailed here. Leave blank to turn off.</p>
              </div>
              </>}
              {company?.legacy_sync && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-2" data-testid="resync-section">
                <p className="text-sm font-medium">Sync data from your old app</p>
                <p className="text-xs text-muted-foreground">Replaces the jobs, crew, timesheets, clients and roster in THIS app with everything from your original app. Use this if the app is showing test data or missing jobs.</p>
                {!confirmResync ? (
                  <Button data-testid="resync-data-btn" variant="outline" onClick={() => setConfirmResync(true)} className="rounded-full">Re-sync from old app…</Button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-amber-600">This REPLACES all current data in this app with your old app's data. Continue?</p>
                    <div className="flex gap-2">
                      <Button data-testid="resync-confirm-btn" onClick={resync} disabled={resyncing} className="rounded-full bg-primary hover:bg-primary/90 text-white">{resyncing ? "Syncing…" : "Yes, replace everything"}</Button>
                      <Button variant="ghost" onClick={() => setConfirmResync(false)} disabled={resyncing} className="rounded-full">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
              )}
            </>
          )}
          {isCoord && (
            <div className="rounded-lg border border-border p-4 space-y-2" data-testid="request-change-section">
              <p className="text-sm font-medium flex items-center gap-2"><MessageSquarePlus className="w-4 h-4 text-primary" /> Need something changed?</p>
              <p className="text-xs text-muted-foreground">Send the Cross Jobs team a request for a change just for your company. Add a screenshot if it helps.</p>
              <Button variant="outline" data-testid="request-change-btn" onClick={() => { onOpenChange(false); setReqOpen(true); }} className="rounded-full">Request a change</Button>
            </div>
          )}
          <div className="rounded-lg border border-border p-4 space-y-2" data-testid="guides-section">
            <p className="text-sm font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> User guides (PDF)</p>
            <p className="text-xs text-muted-foreground">Open, print or share a step-by-step manual.</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {isCoord && (
                <a data-testid="guide-supervisor-link" href="/Supervisor-Manual.pdf?v=2" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary transition-colors">
                  <FileText className="w-3.5 h-3.5" /> Supervisor manual
                </a>
              )}
              <a data-testid="guide-worker-link" href="/Worker-Manual.pdf?v=2" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-secondary transition-colors">
                <FileText className="w-3.5 h-3.5" /> {isCoord ? "Worker manual" : "My user guide"}
              </a>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Signed in as {user?.email}</p>
        </div>
        <DialogFooter>
          <Button data-testid="settings-save-btn" onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {isCoord && <RequestChangeDialog open={reqOpen} onOpenChange={setReqOpen} />}
    {canBrand && <CompanyDetailsDialog open={detailsOpen} onOpenChange={setDetailsOpen} />}
    {isCoord && <JobTypesDialog open={typesOpen} onOpenChange={setTypesOpen} />}
    </>
  );
};
